import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, call, refreshAccessToken, type Schemas, tokens } from './client';

type Me = Schemas['MeResponse'];

export interface SessionState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: Me | null;
  /** MVP: one household per user; the first active membership. */
  household: Me['memberships'][number] | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  reloadMe(): Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

function deviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const reloadMe = useCallback(async () => {
    const data = await call(api.GET('/api/v1/me'));
    setMe(data);
    setStatus('authenticated');
  }, []);

  // Bootstrap: the HttpOnly refresh cookie (if any) gets us a fresh access token.
  useEffect(() => {
    void (async () => {
      try {
        if (await refreshAccessToken()) await reloadMe();
        else setStatus('anonymous');
      } catch {
        setStatus('anonymous');
      }
    })();
  }, [reloadMe]);

  useEffect(() => {
    const unsubscribe = tokens.onAuthLost(() => {
      setMe(null);
      setStatus('anonymous');
      queryClient.clear();
    });
    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await call(api.POST('/api/v1/auth/login', { body: { email, password, clientType: 'WEB', deviceLabel: deviceLabel() } }));
      tokens.set(data.accessToken);
      await reloadMe();
    },
    [reloadMe],
  );

  const logout = useCallback(async () => {
    try {
      await call(api.POST('/api/v1/auth/logout'));
    } finally {
      tokens.clear();
      queryClient.clear();
      setMe(null);
      setStatus('anonymous');
    }
  }, [queryClient]);

  const value = useMemo<SessionState>(
    () => ({ status, me, household: me?.memberships[0] ?? null, login, logout, reloadMe }),
    [status, me, login, logout, reloadMe],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/** The current household (routes under the app shell guarantee one exists). */
export function useHousehold() {
  const { household, me } = useSession();
  if (!household || !me) throw new Error('useHousehold used without a household');
  return { familyId: household.familyId, memberId: household.memberId, isOwner: household.isOwner, me };
}
